import React, { useMemo, useRef, } from 'react';
import { Text, Image, Wrap, WrapItem, VStack } from '@chakra-ui/react';
import { localise } from '../../../services/lang/lang';
import { makeMed4 } from '../../../styles/textStyles';
import FormFieldErrors from '../FormFieldErrors/FormFieldErrors';
import MaxLengthInputControl from './MaxLengthInputControl';
import { useTokens } from '../../../context/TokenProvider';
import { RenderIf } from '../RenderIf/RenderIf';
import UploadButton from '../UploadButton/UploadButton';
import BinButton from '../FindingsActionMenu/EditFinding/BinButton';
import PhotoBox from '../Photos/PhotoBox';
const StandardFormMediaUpload = ({ maxFiles = 10, error, files, label, onChange, subText, onDuplicateFilesAdded, onUploadMoreThanMax, isDisabled, ...rest }) => {
    const inputRef = useRef(null);
    const tokens = useTokens();
    const resetInput = () => {
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };
    const handleFilesChange = (event) => {
        const selectedFiles = Array.from(event.target.files ?? []);
        const availableSlots = maxFiles - files.length;
        const dedupedFiles = selectedFiles.filter((newFile) => !files.some((existingFile) => existingFile.name === newFile.name &&
            existingFile.size === newFile.size &&
            existingFile.lastModified === newFile.lastModified));
        const duplicateCount = selectedFiles.length - dedupedFiles.length;
        if (duplicateCount) {
            onDuplicateFilesAdded?.(duplicateCount);
        }
        if (dedupedFiles.length > availableSlots) {
            onUploadMoreThanMax?.();
        }
        if (availableSlots <= 0)
            return;
        const filesToAdd = dedupedFiles.slice(0, availableSlots);
        onChange([...files, ...filesToAdd]);
        resetInput();
    };
    const handleClick = () => {
        inputRef.current?.click();
    };
    const handleRemoveFile = (fileToRemove) => {
        const updatedFiles = files.filter((file) => file !== fileToRemove);
        onChange(updatedFiles);
        resetInput();
    };
    const filesWithPreview = useMemo(() => files.map((file) => ({
        file,
        url: URL.createObjectURL(file),
    })), [files]);
    return (React.createElement(MaxLengthInputControl, { isInvalid: !!error, maxLength: maxFiles, length: files.length, label: label },
        React.createElement(VStack, { "data-testid": "standard_form_media_upload", align: "flex-start", width: "100%" },
            React.createElement(Wrap, { spacing: tokens.Spacing04 }, filesWithPreview.map(({ file, url }) => (React.createElement(WrapItem, { "data-testid": "image_wrap_item", key: `${file.name}-${file.size}-${file.lastModified}` },
                React.createElement(PhotoBox, null,
                    React.createElement(Image, { src: url, alt: `preview-${file.name}`, objectFit: "cover", borderRadius: tokens.BorderRadius02, boxSize: "100%" }),
                    React.createElement(BinButton, { position: "absolute", bottom: 1, right: 1, "aria-label": "Remove image", onClick: () => handleRemoveFile(file) })))))),
            React.createElement("input", { ref: inputRef, type: "file", multiple: true, "data-testid": "standard_form_media_upload__input", hidden: true, onChange: handleFilesChange, ...rest }),
            React.createElement(UploadButton, { isDisabled: isDisabled || files.length === maxFiles, mt: tokens.Spacing02, onClick: handleClick },
                React.createElement(Text, { sx: makeMed4(tokens) }, localise('ADD_MEDIA')))),
        React.createElement(RenderIf, { condition: !!subText },
            React.createElement(Text, { "data-testid": "standard_form_media_upload_sub_text", mt: tokens.Spacing03, variant: "md-600-rg-4" }, subText)),
        React.createElement(FormFieldErrors, { errors: error })));
};
export default StandardFormMediaUpload;
